import { jsx, jsxs } from "react/jsx-runtime";
import { motion } from "motion/react";
import { Shield, Clock, Target, Cloud, Zap, TrendingUp, Users, Bot } from "lucide-react";
const reasons = [
  { icon: Shield, title: "Enterprise Security", desc: "SOC2, GDPR, HIPAA compliant. All data encrypted end-to-end with zero-trust architecture.", color: "#6f5cf7" },
  { icon: Clock, title: "24\xD77 AI Availability", desc: "Never miss a customer. AI handles every query round the clock, even on holidays and weekends.", color: "#8b5cf6" },
  { icon: Target, title: "Industry-Specific AI", desc: "Not generic AI. Purpose-built models trained on your industry's language, workflows & context.", color: "#a855f7" },
  { icon: Cloud, title: "Cloud Native", desc: "Deploy on AWS, GCP, or Azure. Auto-scales to millions of interactions without manual intervention.", color: "#0ea5e9" },
  { icon: Zap, title: "Fast Deployment", desc: "Go live in 14 days, not 14 months. Pre-built connectors for 80+ enterprise systems.", color: "#f59e0b" },
  { icon: TrendingUp, title: "Scalable Architecture", desc: "Handles 10 to 10 million interactions with the same speed, quality, and reliability.", color: "#10b981" },
  { icon: Users, title: "Human Handoff", desc: "AI knows its limits. Seamlessly transfers to human agents with full context when needed.", color: "#ec4899" },
  { icon: Bot, title: "Continuous AI Learning", desc: "Models improve automatically from every interaction, getting smarter without any manual retraining.", color: "#6f5cf7" }
];
function WhySection() {
  return /* @__PURE__ */ jsx("section", { className: "py-24 px-6 bg-white", children: /* @__PURE__ */ jsxs("div", { className: "max-w-7xl mx-auto", children: [
    /* @__PURE__ */ jsxs("div", { className: "text-center mb-16", children: [
      /* @__PURE__ */ jsx(
        motion.div,
        {
          initial: { opacity: 0, y: 14 },
          whileInView: { opacity: 1, y: 0 },
          viewport: { once: true },
          transition: { duration: 0.55 },
          className: "inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-5 text-xs font-semibold",
          style: { backgroundColor: "rgba(111,92,247,0.07)", border: "1px solid rgba(111,92,247,0.18)", color: "#6f5cf7", fontFamily: "'Manrope',sans-serif", letterSpacing: "0.06em" },
          children: "WHY ORRYWORX AI"
        }
      ),
      /* @__PURE__ */ jsx(
        motion.h2,
        {
          initial: { opacity: 0, y: 20 },
          whileInView: { opacity: 1, y: 0 },
          viewport: { once: true },
          transition: { duration: 0.6, delay: 0.05 },
          style: { fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: "clamp(1.8rem,3.5vw,2.75rem)", fontWeight: 800, color: "#111827", letterSpacing: "-0.02em", marginBottom: "0.75rem" },
          children: "Built Different. Built Better."
        }
      ),
      /* @__PURE__ */ jsx(
        motion.p,
        {
          initial: { opacity: 0, y: 14 },
          whileInView: { opacity: 1, y: 0 },
          viewport: { once: true },
          transition: { duration: 0.5, delay: 0.1 },
          style: { fontFamily: "'Inter',sans-serif", fontSize: "1rem", color: "#6b7280", maxWidth: "480px", margin: "0 auto" },
          children: "Eight reasons why 500+ enterprises choose Orryworx AI over generic automation tools."
        }
      )
    ] }),
    /* @__PURE__ */ jsx("div", { className: "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5", children: reasons.map((r, i) => {
      const Icon = r.icon;
      return /* @__PURE__ */ jsxs(
        motion.div,
        {
          initial: { opacity: 0, y: 28 },
          whileInView: { opacity: 1, y: 0 },
          viewport: { once: true },
          transition: { duration: 0.5, delay: i * 0.07 },
          whileHover: { y: -6, boxShadow: `0 20px 48px ${r.color}15` },
          className: "group relative rounded-2xl p-6 bg-white border transition-all duration-300 cursor-default overflow-hidden",
          style: { border: "1px solid #e5e7eb", boxShadow: "0 2px 12px rgba(0,0,0,0.04)" },
          children: [
            /* @__PURE__ */ jsx(
              "div",
              {
                className: "absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-400 pointer-events-none rounded-2xl",
                style: { background: `radial-gradient(circle at 30% 20%,${r.color}06 0%,transparent 70%)` }
              }
            ),
            /* @__PURE__ */ jsx(
              "div",
              {
                className: "w-12 h-12 rounded-2xl flex items-center justify-center mb-5 transition-all duration-300 group-hover:scale-110",
                style: { background: `${r.color}10`, border: `1px solid ${r.color}20` },
                children: /* @__PURE__ */ jsx(Icon, { size: 22, style: { color: r.color } })
              }
            ),
            /* @__PURE__ */ jsx("h3", { style: { fontFamily: "'Plus Jakarta Sans',sans-serif", fontWeight: 700, fontSize: "0.95rem", color: "#111827", marginBottom: "0.5rem", lineHeight: 1.3 }, children: r.title }),
            /* @__PURE__ */ jsx("p", { style: { fontFamily: "'Inter',sans-serif", fontSize: "0.83rem", color: "#6b7280", lineHeight: 1.65 }, children: r.desc }),
            /* @__PURE__ */ jsx(
              "div",
              {
                className: "absolute bottom-0 left-6 right-6 h-0.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300",
                style: { background: `linear-gradient(90deg,${r.color},${r.color}40)` }
              }
            )
          ]
        },
        r.title
      );
    }) })
  ] }) });
}
export {
  WhySection
};
